import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Search, Download, Eye, Check, X, Upload, FileText, Filter } from 'lucide-react';
import { toast } from 'sonner';

const ReceiptsManagement = () => {
  const [receipts, setReceipts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVehicle, setFilterVehicle] = useState('all');
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [receiptsRes, vehiclesRes, usersRes] = await Promise.all([
        axios.get('/receipts'),
        axios.get('/vehicles'),
        axios.get('/admin/users')
      ]);
      setReceipts(receiptsRes.data.receipts || receiptsRes.data);
      setVehicles(vehiclesRes.data.vehicles || vehiclesRes.data);
      setUsers(usersRes.data.users || usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleSyncToLexware = async (receiptId) => {
    try {
      await axios.post(`/receipts/${receiptId}/sync`);
      toast.success('Beleg zu Lexware synchronisiert');
      loadData();
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Fehler beim Synchronisieren');
    }
  };

  const handleBulkSync = async () => {
    const pendingReceipts = filteredReceipts.filter(r => !r.lexware_synced);
    
    if (pendingReceipts.length === 0) {
      toast.info('Keine ausstehenden Belege');
      return;
    }

    if (!window.confirm(`${pendingReceipts.length} Belege zu Lexware synchronisieren?`)) return;

    try {
      await Promise.all(
        pendingReceipts.map(r => axios.post(`/receipts/${r.receipt_id}/sync`))
      );
      toast.success(`${pendingReceipts.length} Belege synchronisiert`);
      loadData();
    } catch (error) {
      console.error('Error bulk syncing:', error);
      toast.error('Fehler beim Massen-Sync');
    }
  };

  const handleExportCSV = () => {
    const csvData = filteredReceipts.map(r => {
      const vehicle = vehicles.find(v => v.vehicle_id === r.vehicle_id);
      const user = users.find(u => u.user_id === r.user_id);
      
      return [
        new Date(r.created_at).toLocaleDateString('de-DE'),
        user?.name || 'Unbekannt',
        vehicle?.license_plate || 'Unbekannt',
        r.type === 'fuel' ? 'Tanken' : 'Einkauf',
        r.amount.toFixed(2),
        r.km_reading || '',
        r.lexware_synced ? 'Ja' : 'Nein'
      ].join(',');
    });

    const headers = ['Datum', 'Fahrer', 'Fahrzeug', 'Typ', 'Betrag', 'KM', 'Synced'];
    const csv = [headers.join(','), ...csvData].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `belege_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('CSV-Export erfolgreich');
  };

  const handlePreview = (receipt) => {
    setPreviewReceipt(receipt);
    setPreviewOpen(true);
  };

  // Filter receipts
  const filteredReceipts = receipts.filter(r => {
    const vehicle = vehicles.find(v => v.vehicle_id === r.vehicle_id);
    const user = users.find(u => u.user_id === r.user_id);
    
    const matchesSearch = 
      vehicle?.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || r.type === filterType;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'synced' && r.lexware_synced) ||
      (filterStatus === 'pending' && !r.lexware_synced);
    const matchesVehicle = filterVehicle === 'all' || r.vehicle_id === filterVehicle;

    return matchesSearch && matchesType && matchesStatus && matchesVehicle;
  });

  // Calculate stats
  const stats = {
    total: receipts.length,
    pending: receipts.filter(r => !r.lexware_synced).length,
    synced: receipts.filter(r => r.lexware_synced).length,
    totalAmount: receipts.reduce((sum, r) => sum + r.amount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="receipts-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Belege-Verwaltung
          </h1>
          <p className="text-zinc-400">Alle Belege verwalten und exportieren</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-zinc-700"
            onClick={handleExportCSV}
            data-testid="export-csv-btn"
          >
            <Download className="mr-2 h-4 w-4" />
            CSV Export
          </Button>
          <Button 
            className="bg-primary hover:bg-primary/90"
            onClick={handleBulkSync}
            data-testid="bulk-sync-btn"
          >
            <Upload className="mr-2 h-4 w-4" />
            Alle zu Lexware
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{stats.total}</div>
              <div className="text-sm text-zinc-400 mt-1">Gesamt Belege</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400">{stats.pending}</div>
              <div className="text-sm text-zinc-400 mt-1">Ausstehend</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{stats.synced}</div>
              <div className="text-sm text-zinc-400 mt-1">Synchronisiert</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">€{stats.totalAmount.toFixed(2)}</div>
              <div className="text-sm text-zinc-400 mt-1">Gesamtwert</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-zinc-400 mb-2 block">
                <Search className="h-3 w-3 inline mr-1" />
                Suche
              </Label>
              <Input
                placeholder="Fahrzeug oder Fahrer..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-2 block">
                <Filter className="h-3 w-3 inline mr-1" />
                Typ
              </Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Alle Typen</SelectItem>
                  <SelectItem value="fuel">Tanken</SelectItem>
                  <SelectItem value="purchase">Einkauf</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-2 block">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Alle Status</SelectItem>
                  <SelectItem value="pending">Ausstehend</SelectItem>
                  <SelectItem value="synced">Synchronisiert</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 mb-2 block">Fahrzeug</Label>
              <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">Alle Fahrzeuge</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.license_plate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-zinc-900">
                  <TableHead className="text-zinc-400">Datum</TableHead>
                  <TableHead className="text-zinc-400">Fahrer</TableHead>
                  <TableHead className="text-zinc-400">Fahrzeug</TableHead>
                  <TableHead className="text-zinc-400">Typ</TableHead>
                  <TableHead className="text-zinc-400">Betrag</TableHead>
                  <TableHead className="text-zinc-400">KM</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReceipts.map((receipt) => {
                  const vehicle = vehicles.find(v => v.vehicle_id === receipt.vehicle_id);
                  const user = users.find(u => u.user_id === receipt.user_id);
                  
                  return (
                    <TableRow key={receipt.receipt_id} className="border-zinc-800 hover:bg-zinc-800">
                      <TableCell className="text-zinc-300">
                        {new Date(receipt.created_at).toLocaleDateString('de-DE')}
                      </TableCell>
                      <TableCell className="text-zinc-300">{user?.name || 'Unbekannt'}</TableCell>
                      <TableCell className="font-mono text-zinc-300">{vehicle?.license_plate || 'Unbekannt'}</TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {receipt.type === 'fuel' ? 'Tanken' : 'Einkauf'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono font-bold text-white">€{receipt.amount.toFixed(2)}</TableCell>
                      <TableCell className="font-mono text-zinc-400">
                        {receipt.km_reading ? `${receipt.km_reading.toLocaleString()} km` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={receipt.lexware_synced ? 'default' : 'secondary'}>
                          {receipt.lexware_synced ? (
                            <><Check className="h-3 w-3 mr-1 inline" />Synced</>
                          ) : (
                            <><X className="h-3 w-3 mr-1 inline" />Pending</>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(receipt)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {!receipt.lexware_synced && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => handleSyncToLexware(receipt.receipt_id)}
                            >
                              <Upload className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {filteredReceipts.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              {searchTerm || filterType !== 'all' || filterStatus !== 'all' || filterVehicle !== 'all'
                ? 'Keine Belege mit diesen Filtern gefunden'
                : 'Noch keine Belege vorhanden'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Beleg-Details</DialogTitle>
          </DialogHeader>
          {previewReceipt && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-zinc-500">Datum</div>
                  <div className="text-white font-medium">
                    {new Date(previewReceipt.created_at).toLocaleString('de-DE')}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500">Typ</div>
                  <div className="text-white font-medium capitalize">
                    {previewReceipt.type === 'fuel' ? 'Tanken' : 'Einkauf'}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500">Betrag</div>
                  <div className="text-white font-bold text-lg">€{previewReceipt.amount.toFixed(2)}</div>
                </div>
                <div>
                  <div className="text-zinc-500">Kilometerstand</div>
                  <div className="text-white font-medium">
                    {previewReceipt.km_reading ? `${previewReceipt.km_reading.toLocaleString()} km` : '-'}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500">Fahrzeug</div>
                  <div className="text-white font-medium">
                    {vehicles.find(v => v.vehicle_id === previewReceipt.vehicle_id)?.license_plate || 'Unbekannt'}
                  </div>
                </div>
                <div>
                  <div className="text-zinc-500">Fahrer</div>
                  <div className="text-white font-medium">
                    {users.find(u => u.user_id === previewReceipt.user_id)?.name || 'Unbekannt'}
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-zinc-800">
                <div className="text-zinc-500 mb-2">Beleg-Foto</div>
                <div className="bg-zinc-800 rounded-lg p-8 text-center">
                  <FileText className="h-16 w-16 text-zinc-600 mx-auto mb-2" />
                  <div className="text-zinc-400 text-sm">Foto-Preview</div>
                  <div className="text-xs text-zinc-600 mt-1">{previewReceipt.image_path}</div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReceiptsManagement;
